import BaseMap from 'components/BaseMap/BaseMap';
import MapCenterReporter from 'components/BaseMap/components/MapCenterReporter';
import { getAlertSeverityColor } from 'features/Weather/features/WeatherAlerts/utils/alertSeverity.utils';
import useLocation from 'hooks/useLocation';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetFemaJurisdictionsQuery } from 'services/ipaws/ipaws.api';
import {
  selectActiveAlertDraft,
  selectAlertMode,
  selectGetMapCenter,
  selectIpawsFemaEnvironment,
  selectMapCenter,
  selectZoom,
} from '../../state/selectors/alertCreatorSelectors';
import { setGetMapCenter } from '../../state/slices/alertCreatorSlice';
import { ALERT_MODES, getAlertMapSeverityValue } from '../../utils/alertCreator.utils';
import { acceptFemaJurisdictionsForClass } from '../../utils/femaEnvironment.utils';
import JurisdictionMapOverlay from '../../../../components/JurisdictionMapOverlay/JurisdictionMapOverlay';
import './assets/alertMapEditor.scss';
import IpawsAreaMapContent from './components/IpawsAreaMapContent/IpawsAreaMapContent';
import RescAlertMapContent from './components/RescAlertMapContent/RescAlertMapContent';
import { getAlertMapFitRequest, JURISDICTION_FIT_PADDING } from './utils/alertMapFit.utils';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const NO_JURISDICTIONS = [];

/**
 * Provides the shared map shell, then shows the area editor for the active alert type.
 *
 * @param {Object} props
 * @param {boolean} [props.fillContainer=false] Whether the map should fill its parent.
 * @returns {JSX.Element|null}
 */
const AlertMapEditor = ({ fillContainer = false }) => {
  const { jurisdictionPolygons = NO_JURISDICTIONS } = useLocation();
  const alertData = useSelector(selectActiveAlertDraft);
  const alertMode = useSelector(selectAlertMode);
  const mapCenter = useSelector(selectMapCenter);
  const currentMapCenter = useSelector(selectGetMapCenter);
  const zoom = useSelector(selectZoom);
  const dispatch = useDispatch();
  const severityColor = getAlertSeverityColor(getAlertMapSeverityValue(alertData?.severity));
  const jurisdictionBoundaryRings = alertData?.resolvedJurisdiction?.boundaryRings;
  // The areas the COG may alert, drawn in IPAWS mode only (card BE-13). Multi-COG contract §1:
  // the query asks for the chosen class, and an answer that echoes another class is not drawn.
  const femaEnvironment = useSelector(selectIpawsFemaEnvironment);
  const femaJurisdictions = useGetFemaJurisdictionsQuery(
    femaEnvironment ? { environment: femaEnvironment } : undefined,
    { skip: alertMode !== ALERT_MODES.IPAWS },
  );
  const authorizedJurisdictions =
    acceptFemaJurisdictionsForClass(femaJurisdictions.data, femaEnvironment).data?.jurisdictions ??
    NO_JURISDICTIONS;
  // One fit request for both alert types (card FE-05). Opening the creator, a change of alert type,
  // and a change of the selected jurisdiction each give a new key, and the map fits that
  // jurisdiction once. Shape edits, undo, redo, placement, and channel changes keep the key, so
  // the operator keeps the pan and zoom they chose (bug 4214).
  const fitRequest = getAlertMapFitRequest({
    alertMode,
    draft: alertData,
    authorizedJurisdictions,
    isAuthorizedListLoading: Boolean(femaJurisdictions.isLoading),
    workplacePolygons: jurisdictionPolygons,
  });
  // New bounds with the same key never fit again, so the bounds are kept stable for each key.
  const fitBoundsKey = JSON.stringify(fitRequest.bounds ?? null);
  const fitBounds = useMemo(() => JSON.parse(fitBoundsKey) ?? undefined, [fitBoundsKey]);

  if (!alertData) return null;

  return (
    <div
      className={`alert-creator-map bg-primary min-h-0 overflow-hidden rounded-3 ${
        fillContainer ? 'w-100 h-100' : 'flex-grow-full ratio-1'
      }`}
    >
      <BaseMap
        mapCenter={mapCenter}
        zoom={zoom}
        bounds={fitBounds}
        boundsFitKey={fitRequest.key}
        boundsPadding={JURISDICTION_FIT_PADDING}
      >
        <MapCenterReporter onCenterChange={(center) => dispatch(setGetMapCenter(center))} />

        {alertMode === ALERT_MODES.IPAWS ? (
          <IpawsAreaMapContent
            alertData={alertData}
            authorizedJurisdictions={authorizedJurisdictions}
            currentMapCenter={currentMapCenter}
            severityColor={severityColor}
          />
        ) : (
          <RescAlertMapContent
            coordinates={alertData.coordinates}
            currentMapCenter={currentMapCenter}
            severityColor={severityColor}
          />
        )}

        <JurisdictionMapOverlay
          workplacePolygons={jurisdictionPolygons}
          femaBoundaryRings={
            alertMode === ALERT_MODES.IPAWS ? (jurisdictionBoundaryRings ?? []) : []
          }
        />
      </BaseMap>
    </div>
  );
};

export default AlertMapEditor;
